import React from 'react';
import { Badge } from '../ui/badge';
import { Lock, Unlock, ShieldCheck, ShieldAlert, Ban, FileWarning } from 'lucide-react';

/**
 * Chip visual del estado fiscal (Verifactu/SIF) de una factura.
 * Recibe el objeto `fiscal_state` que devuelve el backend (puede ser null).
 *
 * Estados posibles:
 *  - null / undefined  → "Borrador" (gris) — la factura aún no ha sido emitida fiscalmente.
 *  - estado=borrador   → "Borrador"
 *  - estado=emitida modo=VERIFACTU congelada=true → "Emitida (Verifactu)" verde
 *  - estado=emitida legacy=true                   → "Emitida (Legacy)" azul tenue
 *  - estado=anulada                               → "Anulada" rojo
 *  - estado=enviada                               → "Enviada AEAT" verde fuerte
 *  - estado=rechazada                             → "Rechazada AEAT" naranja
 */
export const FiscalStateBadge = ({ fiscalState, size = 'sm' }) => {
  const fs = fiscalState;
  let cfg;

  if (!fs || fs.estado_fiscal === 'borrador') {
    cfg = { label: 'Borrador', color: 'bg-gray-100 text-gray-700 border-gray-300', Icon: Unlock };
  } else if (fs.estado_fiscal === 'emitida' && fs.modo === 'VERIFACTU') {
    cfg = { label: 'Emitida · Verifactu', color: 'bg-emerald-100 text-emerald-800 border-emerald-300', Icon: ShieldCheck };
  } else if (fs.estado_fiscal === 'emitida' && fs.legacy) {
    cfg = { label: 'Emitida · Legacy', color: 'bg-sky-100 text-sky-800 border-sky-300', Icon: Lock };
  } else if (fs.estado_fiscal === 'emitida') {
    cfg = { label: 'Emitida', color: 'bg-sky-100 text-sky-800 border-sky-300', Icon: Lock };
  } else if (fs.estado_fiscal === 'enviada') {
    cfg = { label: 'Enviada AEAT', color: 'bg-green-100 text-green-800 border-green-300', Icon: ShieldCheck };
  } else if (fs.estado_fiscal === 'rechazada') {
    cfg = { label: 'Rechazada AEAT', color: 'bg-orange-100 text-orange-800 border-orange-300', Icon: ShieldAlert };
  } else if (fs.estado_fiscal === 'anulada') {
    cfg = { label: 'Anulada', color: 'bg-red-100 text-red-800 border-red-300', Icon: Ban };
  } else if (fs.estado_fiscal === 'rectificada') {
    cfg = { label: 'Rectificada', color: 'bg-purple-100 text-purple-800 border-purple-300', Icon: FileWarning };
  } else {
    cfg = { label: fs.estado_fiscal, color: 'bg-gray-100 text-gray-700 border-gray-300', Icon: Unlock };
  }

  const { Icon } = cfg;
  const padding = size === 'xs' ? 'px-1.5 py-0' : 'px-2 py-0.5';
  return (
    <Badge
      variant="outline"
      className={`${cfg.color} ${padding} font-medium border`}
      data-testid="fiscal-state-badge"
      title={fs?.modo ? `Modo: ${fs.modo}` : 'Sin estado fiscal'}
    >
      <Icon className="w-3 h-3 mr-1" />
      {cfg.label}
    </Badge>
  );
};

export default FiscalStateBadge;
